import { Component, DestroyRef, EventEmitter, Input, Output } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormArray, FormControl, FormGroup } from '@angular/forms'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { finalize, forkJoin, Observable } from 'rxjs'
import { environment } from 'src/environments/environment'
import { ProgressCircleComponent } from '../progress-circle/progress-circle.component'
import { DeleteModalComponent } from './components/delete-modal/delete-modal.component'
import { GalleryComponent } from './components/gallery/gallery.component'
import { MultipleFileUploadComponent } from './components/multiple-file-upload/multiple-file-upload.component'
import { UploadInputComponent } from './components/upload-input/upload-input.component'
import { UploadedFileComponent } from './components/uploaded-file/uploaded-file.component'
import { IsImagePipe } from './pipes/is-image.pipe'
import { UploadService } from './services/upload.service'
import { Dimension } from './types/upload-dimensions.type'
import { ApiPayload, FileRequest, FileResponse, UploadedFileList } from './types/upload.type'

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss'],
  standalone: true,
  imports: [NgxTippyModule, ProgressCircleComponent, UploadedFileComponent, UploadInputComponent, DeleteModalComponent, GalleryComponent, MultipleFileUploadComponent],
  providers: [UploadService, IsImagePipe]
})
export class UploadComponent {
  public fileList: UploadedFileList = new FormArray([new FormGroup({})])
  public isEditing: boolean = false
  public isGalleryOpened: boolean = false
  public selectedFileIndex?: number
  public uploadProgress: number = 0
  public cropperModalId: string = crypto.randomUUID()
  public deleteModalId: string = crypto.randomUUID()
  public warningMessage: string = ''
  public uploadedImageToCrop?: File
  public uploadProgressList: number[] = []
  // Disable file name till handling it from Backend
  public canEditFileName: boolean = false
  public displayFilename: boolean = false

  @Input('fileList')
  set fileListValue(value: UploadedFileList) {
    if (!this.fileList) return
    this.fileList = value
    this._removeEmptyValues()
    this._initializeInputs()
  }
  @Input() label: string = 'Upload your file'
  @Input() hint: string = ''
  @Input() accept: string = ''
  @Input() deleteFileWithAPI: boolean = false
  @Input() isMultipleInput: boolean = false
  @Input() showIcon: boolean = true
  @Input() maxFilesToUpload: number = 1
  @Input() minFileSize: number = 30
  @Input() maxFileSize: number = 2 * 1024
  @Input() uploadedImageDimensions: Dimension = {
    maxWidth: 0,
    maxHeight: 0,
    minHeight: 0,
    minWidth: 0
  }
  @Input() height: string = '110px'
  @Input() width: string = '110px'
  @Input() componentMinHeight: string = '136px'
  @Input() apiPayload?: ApiPayload
  @Input() tooltip: string = ''
  @Input() useGallery: boolean = true

  @Output() fileChanged: EventEmitter<FileRequest> = new EventEmitter<FileRequest>()
  @Output() fileDeleted: EventEmitter<FileRequest> = new EventEmitter<FileRequest>()

  constructor(private _ModalService: ModalService, private _UploadService: UploadService, private _AlertService: AlertService, private _destroyRef: DestroyRef) {}

  public get filesObjects(): FormGroup[] {
    return this.multiFileList.controls as FormGroup[]
  }

  public get isSingleUpload(): boolean {
    return this.fileList instanceof FormGroup
  }

  public get singleFileList(): FormGroup {
    return this.fileList as FormGroup
  }

  public get multiFileList(): FormArray {
    return this.fileList as FormArray
  }

  /**
   * @description Remove all empty values (all objects with no original_path are considered as empty)
   */
  private _removeEmptyValues(): void {
    if (this.fileList instanceof FormGroup && !this.fileList.value.path) {
      Object.keys(this.fileList.controls).forEach((controlName) => {
        ;(this.fileList as FormGroup).removeControl(controlName)
      })
      this.fileList.addControl('path', new FormControl('', this.fileList.validator))
      this.uploadProgressList = [0]
    } else if (this.fileList instanceof FormArray)
      this.fileList.controls.forEach((formGroup, index) => {
        // Remove the domain by default
        if (formGroup instanceof FormGroup) {
          formGroup.get('path')?.setValue(formGroup.get('path')?.value.replace(environment.s3_files_root, ''))
          formGroup.get('cropped_path')?.setValue(formGroup.get('cropped_path')?.value.replace(environment.s3_files_root, ''))
        }
        if (formGroup instanceof FormGroup && !formGroup.value.path) (this.fileList as FormArray).removeAt(+index)
        else this.uploadProgressList.push(0)
      })
  }

  /**
   * @description Initialize Limit input & isMultipleInput values
   */
  private _initializeInputs(): void {
    if (this.fileList instanceof FormArray) {
      this.maxFilesToUpload = this.maxFilesToUpload > 1 ? this.maxFilesToUpload : this.fileList.value.length || 2
      return
    }
    if (this.fileList.validator) this.getFormControl('path')?.addValidators([this.fileList.validator])
    this.maxFilesToUpload = 1
    this.isMultipleInput = false
    this.displayFilename = this.canEditFileName
  }

  /**
   * @description Handle File change after choose or drop file/s
   * @param files File[]
   */
  public onFileChanged(files: File[]): void {
    this.warningMessage = this._UploadService.checkFilesLimit(this.fileList, files, this.maxFilesToUpload)
    if (this.warningMessage) return
    const observables: Observable<File>[] = []
    for (let file of files) {
      this.warningMessage =
        this._UploadService.checkFileSizeValidation(file, this.maxFileSize, this.minFileSize) || this._UploadService.checkSupportedTypeValidation(file, this.accept)
      if (this.warningMessage) {
        this.fileList.markAllAsTouched()
        return
      }
      observables.push(this._handleUploadedFile(file))
    }
    forkJoin(observables).pipe(takeUntilDestroyed(this._destroyRef)).subscribe()
  }

  /**
   * @description Validate on file dimension and upload it if it's valid
   * @param file File
   * @param isSingle boolean
   * @returns Observable<File>
   */
  private _handleUploadedFile(file: File): Observable<File> {
    return new Observable<File>((observer) => {
      if (this._UploadService.isImageType(file)) {
        const reader = new FileReader()
        reader.onload = () => {
          const img = new Image()
          ;(<string | ArrayBuffer | null>img.src) = reader.result
          img.onload = () => {
            this.warningMessage = this._UploadService.checkImageDimensionsValidation(img, this.uploadedImageDimensions)
            if (this.warningMessage) {
              observer.error()
              return
            }
            this._uploadFile(file)
            observer.next(file)
            observer.complete()
          }
        }
        reader.readAsDataURL(file)
      } else {
        this._uploadFile(file)
        observer.next(file)
        observer.complete()
      }
    })
  }

  /**
   * @description Handle Upload the files with API
   * @param file File
   */
  private _uploadFile(file: File): void {
    //before upload
    let objectToDelete: FileRequest | undefined
    let index: number = this.selectedFileIndex || 0
    if (this.isEditing) {
      this.uploadProgressList[index] = 1
      objectToDelete = this.isSingleUpload ? this.singleFileList.value : this.multiFileList.at(index).value
    } else {
      if (this.isSingleUpload) {
        this.uploadProgressList[0] = 1
      } else {
        this.uploadProgressList.push(1)
        index = this.uploadProgressList.length - 1
        ;(this.fileList as FormArray).push(
          new FormGroup({
            path: new FormControl(''),
            name: new FormControl(''),
            id: new FormControl('')
          })
        )
      }
    }
    forkJoin(this._UploadService.prepareUploadAPIs(file, index, this.apiPayload, this.uploadProgressList))
      .pipe(
        finalize(() => {
          this.uploadedImageToCrop = undefined
          this.selectedFileIndex = this.deleteFileWithAPI && this.isEditing && objectToDelete?.id ? this.selectedFileIndex : undefined
          this.isEditing = false
          this.fileList.markAllAsTouched()
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: ([fileRes, originalFileRes]) => {
          if (typeof fileRes === 'object' && 'data' in fileRes) {
            this._UploadService
              .getFile(fileRes.data.uploads.name)
              .pipe(takeUntilDestroyed(this._destroyRef))
              .subscribe({
                next: (res) => {
                  fileRes.data.uploads.path = res.url
                  const response = [fileRes.data.uploads]
                  if (typeof originalFileRes === 'object') response.push(originalFileRes.data.uploads)
                  if (this.isEditing) {
                    this.fileList.markAsDirty()
                    this.fileDeleted.emit(objectToDelete)
                    if (objectToDelete?.name && this.deleteFileWithAPI) this._deleteFileWithApiCall(objectToDelete.name, false)
                  }
                  if (this.isSingleUpload) {
                    this._patchData(this.singleFileList, response, this._UploadService.isImageType(file))
                  } else {
                    this._patchData(this.multiFileList.at(index) as FormGroup, response, this._UploadService.isImageType(file))
                  }
                  // Clear progress only after data is patched successfully
                  this.uploadProgressList[index] = 0
                },
                error: () => {
                  this.uploadProgressList[index] = 0
                }
              })
          } else {
            // If fileRes is not valid, clear progress anyway
            this.uploadProgressList[index] = 0
          }
        },
        error: () => {
          this.uploadProgressList[index] = 100
          ;(this.fileList as FormArray)?.removeAt(index)
        }
      })
  }

  /**
   * @description Patch the files list data
   * @param object FormGroup
   * @param data FileResponse[]
   * @param isImage boolean
   */
  private _patchData(object: FormGroup, data: FileResponse[], isImage: boolean): void {
    object.patchValue({
      id: data[0].id,
      name: data[0].name,
      path: data[0].path
    })
    this.fileList.markAsDirty()
    this.fileChanged.emit(object.value)
  }

  /**
   * @description Toggle a modal with an id
   * @param modalId string
   */
  public toggleModal(modalId: string): void {
    this._ModalService.toggleModal(modalId)
  }

  /**
   * @description Check if a modal is opened
   * @param modalId string
   * @returns boolean
   */
  public isModalOpened(modalId: string): boolean {
    return this._ModalService.isModalOpen(modalId)
  }

  /**
   * @description Handle delete file and open delete file modal
   * @param index number
   */
  public deleteFile(index: number): void {
    this.selectedFileIndex = index
    this.toggleModal(this.deleteModalId)
  }

  /**
   * @description Confirm on deleting file
   */
  public confirmDeleting(): void {
    this.toggleModal(this.deleteModalId)

    if (this.fileList instanceof FormGroup) {
      if (this.deleteFileWithAPI && this.fileList.value?.name) this._deleteFileWithApiCall(this.fileList.value.name)
      else this._applyDelete(true)
    } else {
      if (this.deleteFileWithAPI && this.fileList.at(this.selectedFileIndex as number).value?.name)
        this._deleteFileWithApiCall(this.fileList.at(this.selectedFileIndex as number).value.name)
      else this._applyDelete(false)
    }
  }

  /**
   * @description Delete a file with api call
   * @param id number
   * @param applyDelete boolean
   */
  private _deleteFileWithApiCall(fileName: string, applyDelete: boolean = true): void {
    this._UploadService
      .deleteFile(fileName)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => {
          if (!applyDelete) return
          this._applyDelete(this.fileList instanceof FormGroup)
          this._AlertService.alert(Alert.SUCCESS, res?.message || 'Your file has been deleted successfully')
        }
      })
  }

  /**
   * @description Apply the delete on the file list and emit its value
   * @param isSingleUpload boolean
   */
  private _applyDelete(isSingleUpload: boolean): void {
    this.fileList.markAsDirty()
    this.fileList.markAllAsTouched()
    if (isSingleUpload) {
      this.uploadProgressList[0] = 0
      this.fileDeleted.emit(this.fileList.value)
      this.fileList.reset()
    } else {
      // Get the deleted file value BEFORE removing it from the array
      const deletedFileValue = this.multiFileList.at(this.selectedFileIndex as number).value
      this.uploadProgressList.splice(this.selectedFileIndex as number, 1)
      this.multiFileList.removeAt(this.selectedFileIndex as number)
      this.fileDeleted.emit(deletedFileValue)
    }
    this.selectedFileIndex = undefined
  }

  /**
   * @description Reset values on close cropper modal
   */
  public closeCropperModal(): void {
    this.selectedFileIndex = undefined
    this.uploadedImageToCrop = undefined
    this.isEditing = false
    this.toggleModal(this.cropperModalId)
  }

  /**
   * @description reset values on cancel deleting file
   */
  public cancelDelete(): void {
    this.selectedFileIndex = undefined
  }

  /**
   * @description Toggle the Gallery
   * @param index number | undefined
   */
  public toggleGallery(index?: number): void {
    if (!this.useGallery) return
    this.selectedFileIndex = index
    this.isGalleryOpened = !this.isGalleryOpened
  }

  /**
   * @description Get form control from formGroup
   * @param controlName string
   * @returns FormControl | undefined
   */
  public getFormControl(controlName: string): FormControl | undefined {
    return (this.fileList as FormGroup).get(controlName) as FormControl
  }
}
